import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Teams = () => {
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [userRole, setUserRole] = useState('user');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [selectedManager, setSelectedManager] = useState('');
  const [editingTeam, setEditingTeam] = useState(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [showDeletedTeams, setShowDeletedTeams] = useState(false);
  const [deletedTeams, setDeletedTeams] = useState([]);

  useEffect(() => {
    loadUserData();
    fetchTeams();
  }, []);

  const loadUserData = async () => {
    try {
      const storedUserData = localStorage.getItem('userData');
      if (storedUserData) {
        setIsLoggedIn(true);
        const userData = JSON.parse(storedUserData);
        setUserRole(userData.role || 'user');
        setCurrentUserId(userData.id);

        // Load users if admin/manager
        if (userData.role === 'admin' || userData.role === 'manager') {
          const res = await axios.get('/api/teams/users');
          setUsers(res.data);
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const res = await axios.get('/api/teams');
      setTeams(res.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) return;

    try {
      const teamData = { name: newTeamName };
      if (selectedManager) {
        teamData.managerId = selectedManager;
      }

      await axios.post('/api/teams', teamData);
      setNewTeamName('');
      setSelectedManager('');
      setShowCreateTeam(false);
      fetchTeams();
    } catch (error) {
      console.error('Error creating team:', error);
      alert('Error creating team: ' + (error.response?.data?.error || error.message));
    }
  };

  const addUserToTeam = async (teamId, userId) => {
    try {
      await axios.post(`/api/teams/${teamId}/add-user`, { userId });
      fetchTeams();
      loadUserData(); // Refresh users list to show updated team assignments
    } catch (error) {
      console.error('Error adding user to team:', error);
      alert('Error adding user: ' + (error.response?.data?.error || error.message));
    }
  };

  const removeUserFromTeam = async (teamId, userId) => {
    try {
      await axios.post(`/api/teams/${teamId}/remove-user`, { userId });
      fetchTeams();
      loadUserData();
    } catch (error) {
      console.error('Error removing user from team:', error);
      alert('Error removing user: ' + (error.response?.data?.error || error.message));
    }
  };

  const joinTeam = async (id) => {
    try {
      await axios.post(`/api/teams/${id}/join`);
      fetchTeams();
    } catch (error) {
      console.error('Error joining team:', error);
      alert('Error joining team: ' + (error.response?.data?.error || error.message));
    }
  };

  const leaveTeam = async (id) => {
    try {
      await axios.post(`/api/teams/${id}/leave`);
      fetchTeams();
    } catch (error) {
      console.error('Error leaving team:', error);
      alert('Error leaving team: ' + (error.response?.data?.error || error.message));
    }
  };

  const getAvailableUsers = () => {
    return users.filter(user => !user.team);
  };

  const getFreeAgents = () => {
    return users
      .filter(user => !user.team)
      .sort((a, b) => {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
  };

  const getAvailableManagers = () => {
    return users.filter(user => user.role === 'manager' || user.role === 'admin');
  };

  const updateTeam = async (teamId, newName) => {
    try {
      await axios.put(`/api/teams/${teamId}`, { name: newName });
      fetchTeams();
      setEditingTeam(null);
      setEditTeamName('');
    } catch (error) {
      console.error('Error updating team:', error);
      alert('Error updating team: ' + (error.response?.data?.error || error.message));
    }
  };

  const deleteTeam = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team? All members will be removed from the team.')) return;

    try {
      await axios.delete(`/api/teams/${teamId}`);
      fetchTeams();
    } catch (error) {
      console.error('Error deleting team:', error);
      alert('Error deleting team: ' + (error.response?.data?.error || error.message));
    }
  };

  const addManagerToTeam = async (teamId, managerId) => {
    try {
      await axios.post(`/api/teams/${teamId}/add-manager`, { managerId });
      fetchTeams();
    } catch (error) {
      console.error('Error adding manager to team:', error);
      alert('Error adding manager: ' + (error.response?.data?.error || error.message));
    }
  };

  const removeManagerFromTeam = async (teamId, managerId) => {
    try {
      await axios.post(`/api/teams/${teamId}/remove-manager`, { managerId });
      fetchTeams();
    } catch (error) {
      console.error('Error removing manager from team:', error);
      alert('Error removing manager: ' + (error.response?.data?.error || error.message));
    }
  };

  const fetchDeletedTeams = async () => {
    try {
      const res = await axios.get(`/api/teams/deleted`);
      setDeletedTeams(res.data);
    } catch (error) {
      console.error('Error fetching deleted teams:', error);
    }
  };

  const restoreTeam = async (teamId) => {
    try {
      await axios.post(`/api/teams/${teamId}/restore`);
      fetchTeams();
      fetchDeletedTeams();
    } catch (error) {
      console.error('Error restoring team:', error);
      alert('Error restoring team: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", background: "#031A54", minHeight: "100vh", padding: "20px" }}>
      <div style={{ maxWidth: "1200px", margin: "0 auto", background: "white", borderRadius: "12px", boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)" }}>

        <div style={{ padding: "20px", background: "#f5f5f5", borderBottom: "3px solid #9584AC" }}>
          <h2 style={{ margin: "0 0 20px 0", color: "#1e3c72" }}>Teams</h2>

          {/* Admin Controls */}
          {userRole === 'admin' && (
            <div style={{ marginBottom: "20px", padding: "15px", background: "white", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" }}>
              <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
                {!showCreateTeam ? (
                  <button
                    onClick={() => setShowCreateTeam(true)}
                    style={{ padding: "10px 20px", background: "#2e7d32", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}
                  >
                    ➕ Create New Team
                  </button>
                ) : (
                  <div style={{ flex: 1 }}>
                    <h3>Create New Team</h3>
                    <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "10px" }}>
                      <input
                        type="text"
                        placeholder="Team Name"
                        value={newTeamName}
                        onChange={(e) => setNewTeamName(e.target.value)}
                        style={{ padding: "8px", border: "1px solid #ccc", borderRadius: "4px", flex: 1 }}
                      />
                      <select
                        value={selectedManager}
                        onChange={(e) => setSelectedManager(e.target.value)}
                        style={{ padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                      >
                        <option value="">Select Manager (optional)</option>
                        {getAvailableManagers().map(user => (
                          <option key={user._id} value={user._id}>
                            {user.firstName} {user.lastName} ({user.nickname})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <button
                        onClick={createTeam}
                        style={{ padding: "8px 16px", background: "#1976d2", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "10px" }}
                      >
                        Create Team
                      </button>
                      <button
                        onClick={() => { setShowCreateTeam(false); setNewTeamName(''); setSelectedManager(''); }}
                        style={{ padding: "8px 16px", background: "#666", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    setShowDeletedTeams(!showDeletedTeams);
                    if (!showDeletedTeams) fetchDeletedTeams();
                  }}
                  style={{ padding: "10px 20px", background: "#ff9800", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}
                >
                  {showDeletedTeams ? 'Hide Deleted' : 'Show Deleted'}
                </button>
              </div>
            </div>
          )}

          {/* Teams List */}
          <div>
            {teams.map(team => (
              <div key={team._id} style={{ marginBottom: "20px", padding: "15px", background: "white", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                  <div style={{ flex: 1 }}>
                    {editingTeam === team._id ? (
                      <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                        <input
                          type="text"
                          value={editTeamName}
                          onChange={(e) => setEditTeamName(e.target.value)}
                          style={{ padding: "6px", border: "1px solid #ccc", borderRadius: "4px", flex: 1 }}
                        />
                        <button
                          onClick={() => updateTeam(team._id, editTeamName)}
                          style={{ padding: "6px 12px", background: "#2e7d32", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => { setEditingTeam(null); setEditTeamName(''); }}
                          style={{ padding: "6px 12px", background: "#666", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <h3 style={{ margin: "0", color: "#1e3c72" }}>{team.name}</h3>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: "5px" }}>
                    {userRole === 'admin' && editingTeam !== team._id && (
                      <>
                        <button
                          onClick={() => { setEditingTeam(team._id); setEditTeamName(team.name); }}
                          style={{ padding: "4px 8px", background: "#2196f3", color: "white", border: "none", borderRadius: "3px", cursor: "pointer", fontSize: "12px" }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteTeam(team._id)}
                          style={{ padding: "4px 8px", background: "#d32f2f", color: "white", border: "none", borderRadius: "3px", cursor: "pointer", fontSize: "12px" }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                    {isLoggedIn && (
                      <>
                        <button
                          onClick={() => joinTeam(team._id)}
                          style={{ padding: "6px 12px", background: "#2e7d32", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", marginRight: "5px" }}
                        >
                          Join Team
                        </button>
                        <button
                          onClick={() => leaveTeam(team._id)}
                          style={{ padding: "6px 12px", background: "#d32f2f", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                        >
                          Leave Team
                        </button>
                      </>
                    )}
                  </div>
                </div>

                 {/* Managers */}
                 <div style={{ marginBottom: "10px" }}>
                   <strong>Managers:</strong>
                   {team.managers && team.managers.length > 0 ? (
                     <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
                       {team.managers.map(manager => (
                         <li key={manager._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                           <div style={{ flex: 1 }}>
                             <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                               <span>{manager.firstName} {manager.lastName} ({manager.nickname}) - {manager.role}</span>
                               {manager.email && (
                                 <a
                                   href={`mailto:${manager.email}`}
                                   title={`Email ${manager.firstName}`}
                                   style={{
                                     color: "#1e3c72",
                                     textDecoration: "none",
                                     fontSize: "1rem",
                                     marginLeft: "8px",
                                     transition: "color 0.2s"
                                   }}
                                   onMouseOver={(e) => e.target.style.color = "#0d47a1"}
                                   onMouseOut={(e) => e.target.style.color = "#1e3c72"}
                                 >
                                   ✉️
                                 </a>
                               )}
                             </div>
                             {/* Social Links for Managers */}
                             {manager.socialLinks && (
                               <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "4px" }}>
                                 {manager.socialLinks.youtube && (
                                   <a
                                     href={manager.socialLinks.youtube}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     title="YouTube"
                                     style={{
                                       color: "#ff0000",
                                       textDecoration: "none",
                                       fontSize: "0.9rem",
                                       transition: "transform 0.2s"
                                     }}
                                     onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                                     onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                   >
                                     📺
                                   </a>
                                 )}
                                 {manager.socialLinks.instagram && (
                                   <a
                                     href={manager.socialLinks.instagram}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     title="Instagram"
                                     style={{
                                       color: "#e1306c",
                                       textDecoration: "none",
                                       fontSize: "0.9rem",
                                       transition: "transform 0.2s"
                                     }}
                                     onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                                     onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                   >
                                     📸
                                   </a>
                                 )}
                                 {manager.socialLinks.tiktok && (
                                   <a
                                     href={manager.socialLinks.tiktok}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     title="TikTok"
                                     style={{
                                       color: "#000000",
                                       textDecoration: "none",
                                       fontSize: "0.9rem",
                                       transition: "transform 0.2s"
                                     }}
                                     onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                                     onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                   >
                                     🎵
                                   </a>
                                 )}
                                 {manager.socialLinks.facebook && (
                                   <a
                                     href={manager.socialLinks.facebook}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     title="Facebook"
                                     style={{
                                       color: "#1877f2",
                                       textDecoration: "none",
                                       fontSize: "0.9rem",
                                       transition: "transform 0.2s"
                                     }}
                                     onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                                     onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                   >
                                     📘
                                   </a>
                                 )}
                                 {manager.socialLinks.otherLink && (
                                   <a
                                     href={manager.socialLinks.otherLink}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     title="Other Link"
                                     style={{
                                       color: "#666",
                                       textDecoration: "none",
                                       fontSize: "0.9rem",
                                       transition: "transform 0.2s"
                                     }}
                                     onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                                     onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                   >
                                     🔗
                                   </a>
                                 )}
                               </div>
                             )}
                             {/* Location for Managers */}
                             {(manager.city || manager.state) && (
                               <div style={{ fontSize: "0.8rem", color: "#666", marginTop: "2px" }}>
                                 📍 {manager.city && manager.state ? `${manager.city}, ${manager.state}` : manager.city || manager.state}
                               </div>
                             )}
                           </div>
                           {userRole === 'admin' && team.managers.length > 1 && (
                             <button
                               onClick={() => removeManagerFromTeam(team._id, manager._id)}
                               style={{ padding: "2px 8px", background: "#d32f2f", color: "white", border: "none", borderRadius: "3px", cursor: "pointer", fontSize: "12px" }}
                             >
                               Remove
                             </button>
                           )}
                         </li>
                       ))}
                     </ul>
                   ) : (
                     <p style={{ margin: "5px 0", fontStyle: "italic", color: "#666" }}>No managers assigned</p>
                   )}

                  {/* Add Manager Section (Admin only) */}
                  {userRole === 'admin' && (
                    <div style={{ marginTop: "5px", paddingLeft: "20px" }}>
                      <select
                        onChange={(e) => {
                          if (e.target.value) {
                            addManagerToTeam(team._id, e.target.value);
                            e.target.value = '';
                          }
                        }}
                        style={{ padding: "4px", border: "1px solid #ccc", borderRadius: "3px", fontSize: "12px" }}
                      >
                        <option value="">Add manager</option>
                        {getAvailableManagers().filter(m => !team.managers.some(tm => tm._id === m._id)).map(user => (
                          <option key={user._id} value={user._id}>
                            {user.firstName} {user.lastName} ({user.nickname})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                 {/* Members */}
                 <div style={{ marginBottom: "10px" }}>
                   <strong>Members:</strong>
                   {team.members && team.members.length > 0 ? (
                     <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
                       {team.members.map(member => (
                         <li key={member._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                           <div style={{ flex: 1 }}>
                             <div style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                               <span>{member.firstName} {member.lastName} ({member.nickname})</span>
                               {member.email && (
                                 <a
                                   href={`mailto:${member.email}`}
                                   title={`Email ${member.firstName}`}
                                   style={{
                                     color: "#1e3c72",
                                     textDecoration: "none",
                                     fontSize: "1rem",
                                     marginLeft: "8px",
                                     transition: "color 0.2s"
                                   }}
                                   onMouseOver={(e) => e.target.style.color = "#0d47a1"}
                                   onMouseOut={(e) => e.target.style.color = "#1e3c72"}
                                 >
                                   ✉️
                                 </a>
                               )}
                             </div>
                             {/* Social Links for Members */}
                             {member.socialLinks && (
                               <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "4px" }}>
                                 {member.socialLinks.youtube && (
                                   <a
                                     href={member.socialLinks.youtube}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     title="YouTube"
                                     style={{
                                       color: "#ff0000",
                                       textDecoration: "none",
                                       fontSize: "0.9rem",
                                       transition: "transform 0.2s"
                                     }}
                                     onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                                     onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                   >
                                     📺
                                   </a>
                                 )}
                                 {member.socialLinks.instagram && (
                                   <a
                                     href={member.socialLinks.instagram}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     title="Instagram"
                                     style={{
                                       color: "#e1306c",
                                       textDecoration: "none",
                                       fontSize: "0.9rem",
                                       transition: "transform 0.2s"
                                     }}
                                     onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                                     onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                   >
                                     📸
                                   </a>
                                 )}
                                 {member.socialLinks.tiktok && (
                                   <a
                                     href={member.socialLinks.tiktok}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     title="TikTok"
                                     style={{
                                       color: "#000000",
                                       textDecoration: "none",
                                       fontSize: "0.9rem",
                                       transition: "transform 0.2s"
                                     }}
                                     onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                                     onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                   >
                                     🎵
                                   </a>
                                 )}
                                 {member.socialLinks.facebook && (
                                   <a
                                     href={member.socialLinks.facebook}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     title="Facebook"
                                     style={{
                                       color: "#1877f2",
                                       textDecoration: "none",
                                       fontSize: "0.9rem",
                                       transition: "transform 0.2s"
                                     }}
                                     onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                                     onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                   >
                                     📘
                                   </a>
                                 )}
                                 {member.socialLinks.otherLink && (
                                   <a
                                     href={member.socialLinks.otherLink}
                                     target="_blank"
                                     rel="noopener noreferrer"
                                     title="Other Link"
                                     style={{
                                       color: "#666",
                                       textDecoration: "none",
                                       fontSize: "0.9rem",
                                       transition: "transform 0.2s"
                                     }}
                                     onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                                     onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                                   >
                                     🔗
                                   </a>
                                 )}
                               </div>
                             )}
                             {/* Location for Members */}
                             {(member.city || member.state) && (
                               <div style={{ fontSize: "0.8rem", color: "#666", marginTop: "2px" }}>
                                 📍 {member.city && member.state ? `${member.city}, ${member.state}` : member.city || member.state}
                               </div>
                             )}
                           </div>
                           {(userRole === 'admin' || (userRole === 'manager' && team.managers.some(m => m._id === currentUserId))) && (
                             <button
                               onClick={() => removeUserFromTeam(team._id, member._id)}
                               style={{ padding: "2px 8px", background: "#d32f2f", color: "white", border: "none", borderRadius: "3px", cursor: "pointer", fontSize: "12px" }}
                             >
                               Remove
                             </button>
                           )}
                         </li>
                       ))}
                     </ul>
                   ) : (
                     <p style={{ margin: "5px 0", fontStyle: "italic", color: "#666" }}>No members yet</p>
                   )}
                 </div>

                {/* Add User Section (Admin/Manager only) */}
                {(userRole === 'admin' || (userRole === 'manager' && team.managers.some(m => m._id === currentUserId))) && (
                  <div style={{ borderTop: "1px solid #eee", paddingTop: "10px" }}>
                    <strong>Add User:</strong>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          addUserToTeam(team._id, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      style={{ marginLeft: "10px", padding: "4px", border: "1px solid #ccc", borderRadius: "3px" }}
                    >
                      <option value="">Select user to add</option>
                      {getAvailableUsers().map(user => (
                        <option key={user._id} value={user._id}>
                          {user.firstName} {user.lastName} ({user.nickname})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))}

            {teams.length === 0 && (
              <p style={{ textAlign: "center", color: "#666", fontStyle: "italic" }}>No teams available</p>
            )}

            {/* Deleted Teams Section */}
            {userRole === 'admin' && showDeletedTeams && (
              <div style={{ marginTop: "30px", padding: "20px", background: "#f8f8f8", borderRadius: "8px", border: "2px solid #ff9800" }}>
                <h3 style={{ color: "#e65100", marginBottom: "15px" }}>Deleted Teams</h3>
                {deletedTeams.length === 0 ? (
                  <p style={{ textAlign: "center", color: "#666", fontStyle: "italic" }}>No deleted teams</p>
                ) : (
                  deletedTeams.map(team => (
                    <div key={team._id} style={{ marginBottom: "15px", padding: "10px", background: "white", borderRadius: "6px", boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <strong>{team.name}</strong>
                          <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                            Managers: {team.managers?.map(m => m.nickname).join(', ') || 'None'}
                            | Members: {team.members?.length || 0}
                          </div>
                        </div>
                        <button
                          onClick={() => restoreTeam(team._id)}
                          style={{ padding: "6px 12px", background: "#4caf50", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                        >
                          Restore Team
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Free Agents Section */}
          {(userRole === 'admin' || userRole === 'manager') && getFreeAgents().length > 0 && (
            <div style={{ marginTop: "30px", padding: "20px", background: "#f8f9fa", borderRadius: "8px", border: "2px solid #17a2b8" }}>
              <h3 style={{ color: "#17a2b8", marginBottom: "15px", textAlign: "center" }}>🏃 Free Agents</h3>
              <p style={{ textAlign: "center", color: "#666", marginBottom: "20px", fontSize: "0.9rem" }}>
                Members available to join teams or preferring to play individually
              </p>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "15px" }}>
                {getFreeAgents().map(agent => (
                  <div key={agent._id} style={{
                    background: "white",
                    padding: "15px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #e9ecef"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                      <div style={{ flex: 1 }}>
                        <strong style={{ color: "#1e3c72", fontSize: "1.1rem" }}>
                          {agent.firstName} {agent.lastName}
                        </strong>
                        {agent.nickname && (
                          <span style={{ color: "#666", fontSize: "0.9rem", marginLeft: "8px" }}>
                            ({agent.nickname})
                          </span>
                        )}
                      </div>

                      {/* Email Icon */}
                      {agent.email && (
                        <a
                          href={`mailto:${agent.email}`}
                          title={`Email ${agent.firstName}`}
                          style={{
                            color: "#1e3c72",
                            textDecoration: "none",
                            fontSize: "1.2rem",
                            marginLeft: "10px",
                            transition: "color 0.2s"
                          }}
                          onMouseOver={(e) => e.target.style.color = "#0d47a1"}
                          onMouseOut={(e) => e.target.style.color = "#1e3c72"}
                        >
                          ✉️
                        </a>
                      )}
                    </div>

                    {/* Social Links */}
                    {agent.socialLinks && (
                      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                        {agent.socialLinks.youtube && (
                          <a
                            href={agent.socialLinks.youtube}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="YouTube"
                            style={{
                              color: "#ff0000",
                              textDecoration: "none",
                              fontSize: "1.1rem",
                              transition: "transform 0.2s"
                            }}
                            onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                            onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                          >
                            📺
                          </a>
                        )}
                        {agent.socialLinks.instagram && (
                          <a
                            href={agent.socialLinks.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Instagram"
                            style={{
                              color: "#e1306c",
                              textDecoration: "none",
                              fontSize: "1.1rem",
                              transition: "transform 0.2s"
                            }}
                            onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                            onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                          >
                            📸
                          </a>
                        )}
                        {agent.socialLinks.tiktok && (
                          <a
                            href={agent.socialLinks.tiktok}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="TikTok"
                            style={{
                              color: "#000000",
                              textDecoration: "none",
                              fontSize: "1.1rem",
                              transition: "transform 0.2s"
                            }}
                            onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                            onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                          >
                            🎵
                          </a>
                        )}
                        {agent.socialLinks.facebook && (
                          <a
                            href={agent.socialLinks.facebook}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Facebook"
                            style={{
                              color: "#1877f2",
                              textDecoration: "none",
                              fontSize: "1.1rem",
                              transition: "transform 0.2s"
                            }}
                            onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                            onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                          >
                            📘
                          </a>
                        )}
                        {agent.socialLinks.otherLink && (
                          <a
                            href={agent.socialLinks.otherLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Other Link"
                            style={{
                              color: "#666",
                              textDecoration: "none",
                              fontSize: "1.1rem",
                              transition: "transform 0.2s"
                            }}
                            onMouseOver={(e) => e.target.style.transform = "scale(1.2)"}
                            onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                          >
                            🔗
                          </a>
                        )}
                      </div>
                    )}

                    {/* Location */}
                    {(agent.city || agent.state) && (
                      <div style={{ marginTop: "8px", fontSize: "0.9rem", color: "#666" }}>
                        📍 {agent.city && agent.state ? `${agent.city}, ${agent.state}` : agent.city || agent.state}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: "15px", textAlign: "center", background: "#f5f5f5", color: "#666", fontSize: "0.85rem" }}>
          <p>Craps Players League ©2025</p>
        </div>
      </div>
    </div>
  );
};

export default Teams;